import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import '../styles/google-meet.css';

const resetOnboarding = async () => {
  try {
    await apiFetch('/api/students/me', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ onboarding_complete: false }),
    });
    window.location.reload();
  } catch {
    alert('Reset failed - check console.');
  }
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const StudentSessions = () => {
  const navigate = useNavigate();
  const [plannedSessions, setPlannedSessions] = useState([]);
  const [meetSessions, setMeetSessions] = useState([]);
  const [whiteboards, setWhiteboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meetLoading, setMeetLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('minimal');
  const [editingMeetId, setEditingMeetId] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingMeetId, setSavingMeetId] = useState(null);
  const [monthDate, setMonthDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/planned-sessions');
      if (!response.ok) {
        setError('Failed to load sessions');
        return;
      }
      const data = await response.json();
      setPlannedSessions(data.sessions || []);
    } catch {
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMeetSessions = useCallback(async () => {
    setMeetLoading(true);
    try {
      const response = await apiFetch('/api/student/google-meet/sessions');
      if (!response.ok) return;
      const data = await response.json();
      setMeetSessions(data.sessions || []);
    } catch (err) {
      console.error('Failed to load Google Meet sessions:', err);
    } finally {
      setMeetLoading(false);
    }
  }, []);

  const fetchWhiteboards = useCallback(async () => {
    try {
      const response = await apiFetch('/api/whiteboards');
      if (!response.ok) return;
      const data = await response.json();
      setWhiteboards(data.whiteboards || []);
    } catch (err) {
      console.error('Failed to load whiteboards:', err);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchMeetSessions();
    fetchWhiteboards();
  }, [fetchSessions, fetchMeetSessions, fetchWhiteboards]);

  const plannedNormalized = useMemo(() => {
    return (plannedSessions || [])
      .map((s) => {
        const startDate = s.scheduled_at ? new Date(s.scheduled_at) : null;
        if (!startDate || isNaN(startDate.getTime())) return null;
        const endDate = new Date(startDate.getTime() + (s.duration_minutes || 60) * 60000);
        return { ...s, __startDate: startDate, __endDate: endDate, __calendarType: 'planned', __calendarKey: `planned-${s.id}` };
      })
      .filter(Boolean)
      .sort((a, b) => a.__startDate - b.__startDate);
  }, [plannedSessions]);

  const pastMeetSessions = useMemo(() => {
    const now = new Date();
    return (meetSessions || [])
      .map((s) => {
        const startDate = s.start_time ? new Date(s.start_time) : null;
        if (!startDate || isNaN(startDate.getTime())) return null;
        const endDate = s.end_time ? new Date(s.end_time) : new Date(startDate.getTime() + 60 * 60000);
        return {
          ...s,
          id: s.transcript_id || s.id,
          title: s.display_title || s.meeting_title || 'Google Meet Session',
          __startDate: startDate,
          __endDate: endDate,
          __calendarType: 'meet',
          __calendarKey: `meet-${s.transcript_id || s.id}`,
        };
      })
      .filter(Boolean)
      .filter((s) => s.__startDate < now)
      .sort((a, b) => b.__startDate - a.__startDate);
  }, [meetSessions]);

  const calendarItems = useMemo(
    () => [...plannedNormalized, ...pastMeetSessions].sort((a, b) => a.__startDate - b.__startDate),
    [plannedNormalized, pastMeetSessions]
  );

  const monthCells = useMemo(() => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const firstVisible = new Date(year, month, 1 - startOffset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(firstVisible);
      d.setDate(firstVisible.getDate() + i);
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const dayEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
      cells.push({
        date: d,
        isCurrentMonth: d.getMonth() === month,
        isToday: d.toDateString() === new Date().toDateString(),
        events: calendarItems.filter((s) => s.__startDate >= dayStart && s.__startDate <= dayEnd),
      });
    }
    return cells;
  }, [calendarItems, monthDate]);

  const upcomingSessions = useMemo(() => {
    const now = new Date();
    return plannedNormalized.filter((s) => s.__startDate >= now).slice(0, 8);
  }, [plannedNormalized]);

  const formatTime = (date) =>
    date ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : 'TBD';

  const splitList = (value) => {
    if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
    return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
  };

  const getParticipantLabel = (session) => {
    const names = splitList(session?.participant_names);
    if (names.length) return names.join(', ');
    const emails = splitList(session?.participant_emails);
    if (emails.length) return emails.join(', ');
    return session?.tutor_email || '';
  };

  const formatRelativeDate = (date) => {
    if (!date) return '';
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diff = Math.round((target - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff < 7) return `In ${diff} days`;
    return target.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const startRename = (session) => {
    setEditingMeetId(session.id);
    setTitleDraft(session.title || '');
  };

  const saveRename = async (session) => {
    const nextTitle = titleDraft.trim();
    if (!nextTitle) return;
    setSavingMeetId(session.id);
    try {
      const response = await apiFetch(`/api/student/google-meet/sessions/${session.id}/display-title`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_title: nextTitle }),
      });
      if (!response.ok) {
        setError('Failed to rename past session');
        return;
      }
      await fetchMeetSessions();
      setEditingMeetId(null);
      setTitleDraft('');
    } catch {
      setError('Failed to rename past session');
    } finally {
      setSavingMeetId(null);
    }
  };

  const handleWhiteboardLink = async (session, whiteboardId) => {
    setSavingMeetId(session.id);
    try {
      const response = await apiFetch(`/api/student/google-meet/sessions/${session.id}/whiteboard`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whiteboard_id: whiteboardId }),
      });
      if (!response.ok) {
        setError('Failed to connect whiteboard');
        return;
      }
      await fetchMeetSessions();
    } catch {
      setError('Failed to connect whiteboard');
    } finally {
      setSavingMeetId(null);
    }
  };

  const monthLabel = monthDate.toLocaleDateString([], { month: 'long', year: 'numeric' });

  return (
    <div className="sessions-page" style={{ overflow: 'auto' }}>
      <div className="sessions-header sessions-header--expanded">
        <div className="sessions-header__content">
          <h1 style={{ margin: 0 }}>Sessions</h1>
          <button
            type="button"
            onClick={() => setViewMode((m) => m === 'minimal' ? 'detailed' : 'minimal')}
            style={{
              marginLeft: 'auto',
              fontSize: '0.78rem',
              padding: '0.35rem 0.7rem',
              background: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              color: '#334155',
              cursor: 'pointer',
            }}
          >
            {viewMode === 'minimal' ? 'Detailed view' : 'Minimal view'}
          </button>
          {process.env.NODE_ENV === 'development' && (
            <button
              onClick={resetOnboarding}
              title="Dev only: re-trigger onboarding modal on next reload"
              style={{
                marginLeft: 'auto',
                fontSize: '0.72rem',
                padding: '0.25rem 0.6rem',
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '5px',
                color: '#92400e',
                cursor: 'pointer',
              }}
            >
              Reset onboarding
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '1.25rem 1rem 2rem' }}>
        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: '#0f172a' }}>
            Schedule
          </h2>

          {error && (
            <div role="alert" style={{ marginBottom: '1rem', color: '#dc2626', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          {loading && <p style={{ color: '#64748b', marginBottom: '1rem' }}>Loading sessions...</p>}

          <section className="google-calendar-layout student-sessions-calendar-layout">
            <aside className="google-calendar-side">
              <article className="google-calendar-side-card">
                <div className="google-calendar-side-head">
                  <h3>Previous Sessions</h3>
                  <span>PAST</span>
                </div>
                <div className="google-calendar-side-list">
                  {meetLoading && pastMeetSessions.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Loading Google Meet sessions...</p>
                  ) : pastMeetSessions.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: '0.875rem' }}>No previous Google Meet sessions yet.</p>
                  ) : (
                    pastMeetSessions.slice(0, 8).map((s) => (
                      <div key={s.id} className="google-calendar-side-item student-past-session-item">
                        <div className="google-calendar-side-item-top">
                          {editingMeetId === s.id ? (
                            <input
                              className="student-session-title-input"
                              value={titleDraft}
                              onChange={(event) => setTitleDraft(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') saveRename(s);
                                if (event.key === 'Escape') setEditingMeetId(null);
                              }}
                              autoFocus
                            />
                          ) : (
                            <strong>{s.title}</strong>
                          )}
                          <span>{formatTime(s.__startDate)}</span>
                        </div>
                        <p>{s.__startDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                        <div className="student-past-session-actions">
                          {editingMeetId === s.id ? (
                            <>
                              <button type="button" className="btn btn-primary btn-small" onClick={() => saveRename(s)} disabled={savingMeetId === s.id || !titleDraft.trim()}>
                                Save
                              </button>
                              <button type="button" className="btn btn-secondary btn-small" onClick={() => setEditingMeetId(null)}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button type="button" className="btn btn-secondary btn-small" onClick={() => startRename(s)}>
                              Rename
                            </button>
                          )}
                        </div>
                        <label className="student-whiteboard-link">
                          <span>Whiteboard</span>
                          <select
                            value={s.whiteboard_id || ''}
                            disabled={savingMeetId === s.id}
                            onChange={(event) => handleWhiteboardLink(s, event.target.value)}
                          >
                            <option value="">Connect existing...</option>
                            {whiteboards.map((board) => (
                              <option key={board.id} value={board.id}>
                                {board.title || 'Untitled Whiteboard'}
                              </option>
                            ))}
                          </select>
                        </label>
                        {s.whiteboard_id && (
                          <button
                            type="button"
                            className="btn btn-primary btn-small"
                            onClick={() => navigate(`/student/whiteboard?board=${encodeURIComponent(s.whiteboard_id)}`)}
                          >
                            Open whiteboard
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </article>

              <article className="google-calendar-side-card">
                <div className="google-calendar-side-head">
                  <h3>Next Sessions</h3>
                  <span>UPCOMING</span>
                </div>
                <div className="google-calendar-side-list">
                  {upcomingSessions.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                      {loading ? 'Loading...' : 'No upcoming sessions yet. Your tutor will schedule sessions here.'}
                    </p>
                  ) : (
                    upcomingSessions.map((s) => (
                      <div key={s.id} className="google-calendar-side-item">
                        <div className="google-calendar-side-item-top">
                          <strong>{s.title}</strong>
                          <span>{formatTime(s.__startDate)}</span>
                        </div>
                        <p>
                          {formatRelativeDate(s.__startDate)}
                          {s.duration_minutes ? ` - ${s.duration_minutes} min` : ''}
                        </p>
                        {viewMode === 'detailed' && getParticipantLabel(s) && (
                          <p style={{ marginTop: '0.25rem', color: '#475569' }}>
                            Participants: {getParticipantLabel(s)}
                          </p>
                        )}
                        {s.description && (
                          <p style={{ marginTop: '0.25rem', fontStyle: 'italic', color: '#64748b' }}>
                            {s.description}
                          </p>
                        )}
                        {s.join_url && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ marginTop: '0.5rem' }}
                            onClick={() => window.open(s.join_url, '_blank', 'noopener,noreferrer')}
                          >
                            Launch session
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </article>
            </aside>

            <article className="google-calendar-main-card">
              <header className="google-calendar-main-header">
                <div className="google-calendar-month-nav">
                  <h2>{monthLabel}</h2>
                  <div className="google-calendar-month-buttons">
                    <button
                      type="button"
                      className="google-calendar-nav-btn"
                      aria-label="Previous month"
                      onClick={() => setMonthDate((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1))}
                    >
                      &lt;
                    </button>
                    <button
                      type="button"
                      className="google-calendar-nav-btn"
                      aria-label="Next month"
                      onClick={() => setMonthDate((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1))}
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              </header>

              <div className="google-calendar-weekdays">
                {WEEKDAY_LABELS.map((d) => <span key={d}>{d}</span>)}
              </div>

              <div className="google-calendar-month-grid">
                {monthCells.map((cell) => {
                  const visible = cell.events.slice(0, 2);
                  const hidden = Math.max(cell.events.length - visible.length, 0);
                  return (
                    <div
                      key={cell.date.toISOString()}
                      className={[
                        'google-calendar-cell',
                        cell.isCurrentMonth ? '' : 'is-outside-month',
                        cell.isToday ? 'is-today' : '',
                      ].join(' ')}
                    >
                      <div className="google-calendar-cell-date">{cell.date.getDate()}</div>
                      <div className="google-calendar-cell-events">
                        {visible.map((s) => (
                          <div
                            key={s.__calendarKey}
                            className={`google-calendar-chip ${s.__calendarType === 'meet' ? 'is-meet-session' : 'is-planned'}`}
                            title={s.title}
                          >
                            <span>{formatTime(s.__startDate)}</span>
                            <strong>{s.title}</strong>
                            {viewMode === 'detailed' && getParticipantLabel(s) && (
                              <em>{getParticipantLabel(s)}</em>
                            )}
                          </div>
                        ))}
                        {hidden > 0 && <span className="google-calendar-more">+{hidden} more</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
          </section>
        </section>

        <section className="detail-empty" style={{ marginBottom: '1rem' }}>
          <div className="empty-card">
            <div className="title">Past Session Notes</div>
            <div className="subtitle">Read-only notes and summaries from completed sessions.</div>
          </div>
        </section>

        <section className="detail-empty">
          <div className="empty-card">
            <div className="title">Assigned Work</div>
            <div className="subtitle">Assignments and follow-up items from your tutor will appear here.</div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StudentSessions;
